// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: speaks one voice-mode reply as it arrives, a sentence at a time. Uses the
// Konvo voice (Kokoro) when that is the chosen voice and falls back to the device
// voice for whatever is left if Kokoro fails; other voices go straight to the device.
import {
	isKonvoVoice,
	type KonvoSpeechStream,
	type KonvoVoicePlayer,
} from '@app/features/konvo_assistant/state/KonvoVoicePlayer';
import VoiceTts from '@app/features/voice/utils/VoiceTtsUtils';

export interface VoiceReplySpeakerOptions {
	voiceId: string;
	rate: number;
	/** The first audio started playing. */
	onStart: () => void;
	/** Everything pushed has been spoken and `end` was called. */
	onDone: () => void;
	/** Switched to the device voice (the orb can't read its level). */
	onDeviceVoice: () => void;
	/** A piece of the reply starts playing, so the caption can mark what is being said. */
	onPiece?: (text: string) => void;
}

export class VoiceReplySpeaker {
	private konvo: KonvoSpeechStream | null = null;
	private deviceVoiceId = '';
	private queue: Array<string> = [];
	private deviceSpeaking = false;
	private ended = false;
	private stopped = false;
	private started = false;

	constructor(
		player: KonvoVoicePlayer | null,
		private readonly options: VoiceReplySpeakerOptions,
	) {
		if (player && isKonvoVoice(options.voiceId)) {
			this.konvo = player.openStream({
				voiceId: options.voiceId,
				rate: options.rate,
				onStart: () => this.markStarted(),
				onPiece: options.onPiece,
				onEnd: () => {
					if (!this.stopped) options.onDone();
				},
				// '' = the device's automatic voice for the rest of the reply.
				onError: (unread) => this.useDevice('', unread),
			});
		} else {
			this.useDevice(options.voiceId, '');
		}
	}

	push(text: string): void {
		if (this.stopped || this.ended || !text.trim()) return;
		if (this.konvo) this.konvo.push(text);
		else {
			this.queue.push(text);
			this.speakNext();
		}
	}

	/** Exactly these audio pieces (as cut on the server, where they are already rendering). */
	pushPieces(pieces: Array<string>): void {
		if (this.stopped || this.ended || pieces.length === 0) return;
		if (this.konvo) this.konvo.pushPieces(pieces);
		else this.push(pieces.join(' '));
	}

	end(): void {
		if (this.stopped || this.ended) return;
		this.ended = true;
		if (this.konvo) this.konvo.end();
		else this.speakNext();
	}

	/** Silence it; nothing more is said and onDone does not fire. */
	stop(): void {
		this.stopped = true;
		this.queue = [];
		if (!this.konvo) VoiceTts.stop();
	}

	private markStarted(): void {
		if (this.started) return;
		this.started = true;
		this.options.onStart();
	}

	private useDevice(voiceId: string, unread: string): void {
		if (this.stopped) return;
		this.konvo = null;
		this.deviceVoiceId = voiceId;
		this.options.onDeviceVoice();
		if (unread.trim()) this.queue.unshift(unread);
		this.speakNext();
	}

	private speakNext(): void {
		if (this.stopped || this.deviceSpeaking) return;
		const next = this.queue.shift();
		if (next === undefined) {
			if (this.ended) this.options.onDone();
			return;
		}
		this.deviceSpeaking = true;
		this.markStarted();
		this.options.onPiece?.(next);
		const done = () => {
			this.deviceSpeaking = false;
			this.speakNext();
		};
		VoiceTts.speak(next, {
			voiceId: this.deviceVoiceId,
			rate: this.options.rate,
			maxLength: Number.POSITIVE_INFINITY,
			onEnd: done,
			onError: done,
			onCancel: () => {
				this.deviceSpeaking = false;
			},
		});
	}
}
